import type {PropertyMap} from "../utils/objects";
import {PersistentTrigger, TimeWindowCondition, type TriggerCondition, type TriggerFactory} from "./triggers";
import {QueryParameters} from "../queryParameters";
import {SessionStorage} from "../clientStorage";
import {Nav} from "../nav";
import {autoRegister, resolve} from "../../container";

type Visits = PropertyMap<boolean>;
const CLIENT_STORAGE_PRESENT_URL_PARAMETER_TRIGGER = "TriggerUrlParameter";

export class UrlParameterTrigger extends PersistentTrigger {
    public constructor(
        protected element: Element,
        private sessionStorage: SessionStorage,
        private queryParameters: QueryParameters,
        private nav: Nav,
        triggerConditions: TriggerCondition[],
        callback: () => void
    ) {
        super(triggerConditions, callback);
        const triggerUrlParameterName = this.element.getAttribute("data-trigger-url-parameter-name") ?? "";

        if (this.triggerUrlParameterIsPresent(triggerUrlParameterName) && this.firstVisit()) {
            this.visit();
            this.queryParameters.remove(triggerUrlParameterName);
            this.trigger();
        }
    }

    private triggerUrlParameterIsPresent(urlParameterName: string): boolean {
        return this.queryParameters.exists(urlParameterName);
    }

    private firstVisit(): boolean {
        return this.storedVisits()[this.nav.pathname()] === undefined;
    }

    private storedVisits(): Visits {
        return this.sessionStorage.fetch<Visits>(CLIENT_STORAGE_PRESENT_URL_PARAMETER_TRIGGER) ?? {};
    }

    private visit(): void {
        const visits = this.storedVisits();
        visits[this.nav.pathname()] = true;
        this.sessionStorage.save(CLIENT_STORAGE_PRESENT_URL_PARAMETER_TRIGGER, visits);
    }
}

@autoRegister()
export class UrlParameterTriggerFactory implements TriggerFactory {
    public constructor(
        private sessionStorage: SessionStorage = resolve(SessionStorage),
        private queryParameters: QueryParameters = resolve(QueryParameters),
        private nav: Nav = resolve(Nav)
    ) {
    }

    public createTrigger(element: Element, callback: () => void): UrlParameterTrigger {
        return new UrlParameterTrigger(element, this.sessionStorage, this.queryParameters, this.nav, TimeWindowCondition.timeWindowCondition(element), callback);
    }
}